import { Icon } from "./Icon.jsx";
// ─── ADVANCED VARIATIONS EDITOR ───────────────────────────────────────────────
// Multi-axis variations for clothing etc.: Colour + Size + a seller-named Custom
// group. Every part is OPTIONAL — a seller can fill just one group, set no add-on
// prices (single product price), and no per-size stock (one overall stock). Used in
// the Add and Edit product forms. Separate from the simple single-list `variations`.
//
// value shape (also what we persist to bloom_products.advanced_variations):
//   { colour:[{name,price}], size:[{name,price,stock}], custom:{label, values:[{name,price}]} }
// price = optional add-on (adds on top of the base price). stock = optional per-size qty.

const EMPTY = { colour: [], size: [], custom: { label: "", values: [] } };

export function emptyAdvanced() { return JSON.parse(JSON.stringify(EMPTY)); }

// True if the seller actually defined anything worth saving.
export function advancedHasContent(v) {
  if (!v) return false;
  const c = (v.colour || []).filter(x => x.name?.trim()).length;
  const s = (v.size   || []).filter(x => x.name?.trim()).length;
  const u = (v.custom?.values || []).filter(x => x.name?.trim()).length;
  return c + s + u > 0;
}

// Strip empty rows before persisting.
export function cleanAdvanced(v) {
  if (!advancedHasContent(v)) return null;
  const clean = (arr, withStock) => (arr || []).filter(x => x.name?.trim()).map(x => ({
    name: x.name.trim(),
    ...(Number(x.price) > 0 ? { price: Number(x.price) } : {}),
    ...(withStock && x.stock !== "" && x.stock != null && !isNaN(parseInt(x.stock)) ? { stock: parseInt(x.stock) } : {}),
  }));
  const out = {};
  const col = clean(v.colour, false);   if (col.length) out.colour = col;
  const siz = clean(v.size, true);      if (siz.length) out.size   = siz;
  const cus = clean(v.custom?.values, false);
  if (cus.length && v.custom?.label?.trim()) out.custom = { label: v.custom.label.trim(), values: cus };
  return Object.keys(out).length ? out : null;
}

// Sum of per-size quantities — only when the seller entered at least one. Mirrors the
// simple-list behaviour (overall stock = total of variant stocks). Returns null when no
// size quantity is set, meaning "use the manually-entered overall stock" instead.
export function advancedSizeStockSum(v) {
  const sizes = (v?.size || []).filter(s => s.name?.trim());
  const anyQty = sizes.some(s => s.stock !== "" && s.stock != null && !isNaN(parseInt(s.stock)));
  if (!anyQty) return null;
  return sizes.reduce((sum, s) => sum + (parseInt(s.stock) || 0), 0);
}

// Normalise a stored value back into editor shape.
export function toEditable(v) {
  if (!v) return emptyAdvanced();
  return {
    colour: (v.colour || []).map(x => ({ name: x.name || "", price: x.price ?? "" })),
    size:   (v.size   || []).map(x => ({ name: x.name || "", price: x.price ?? "", stock: x.stock ?? "" })),
    custom: { label: v.custom?.label || "", values: (v.custom?.values || []).map(x => ({ name: x.name || "", price: x.price ?? "" })) },
  };
}

// Row is defined at MODULE scope (not inside AdvancedVariations) on purpose: a component
// declared inside another component gets a brand-new identity on every parent render, so
// React remounts its <input>s on each keystroke and the field loses focus. Keeping it here
// gives it a stable identity → typing works normally.
function AdvRow({ label, hint, rows, withStock, onRows, addLabel }) {
  return (
    <div style={{ marginBottom: 14 }}>
      {label && <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.05em", color: "var(--ink-soft)", marginBottom: 6 }}>{label}</div>}
      {rows.length > 0 && (
        <div style={{ display: "flex", gap: 6, marginBottom: 4 }}>
          <span style={{ flex: 2, fontSize: 9.5, fontWeight: 700, letterSpacing: "0.04em", color: "var(--ink-soft)" }}>NAME</span>
          <span style={{ flex: 1, fontSize: 9.5, fontWeight: 700, letterSpacing: "0.04em", color: "var(--ink-soft)" }}>+ PRICE (optional)</span>
          {withStock && <span style={{ width: 64, fontSize: 9.5, fontWeight: 700, letterSpacing: "0.04em", color: "var(--ink-soft)" }}>QTY (opt.)</span>}
          <span style={{ width: 30, flexShrink: 0 }} />
        </div>
      )}
      {rows.map((r, i) => (
        <div key={i} style={{ display: "flex", gap: 6, marginBottom: 6, alignItems: "center" }}>
          <input className="inp" placeholder="e.g. Black" value={r.name} onChange={e => onRows(rows.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} style={{ flex: 2, fontSize: 13, padding: "8px 10px" }} />
          <input className="inp" type="number" placeholder="+₹0 (optional)" title="Optional extra price added to the base price" value={r.price} onChange={e => onRows(rows.map((x, j) => j === i ? { ...x, price: e.target.value } : x))} style={{ flex: 1, fontSize: 13, padding: "8px 10px" }} />
          {withStock && <input className="inp" type="number" placeholder="qty" title="Optional stock for this size (blank = use overall stock)" value={r.stock} onChange={e => onRows(rows.map((x, j) => j === i ? { ...x, stock: e.target.value } : x))} style={{ width: 64, fontSize: 13, padding: "8px 10px" }} />}
          <button type="button" onClick={() => onRows(rows.filter((_, j) => j !== i))} style={{ background: "none", border: "1px solid var(--border-mid)", borderRadius: 4, width: 30, height: 30, cursor: "pointer", color: "var(--ink-soft)", flexShrink: 0 }}>×</button>
        </div>
      ))}
      <button type="button" onClick={() => onRows([...rows, withStock ? { name: "", price: "", stock: "" } : { name: "", price: "" }])} style={{ background: "var(--surface)", border: "1px dashed var(--border-mid)", borderRadius: 4, padding: "6px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer", color: "var(--ink)" }}>+ {addLabel}</button>
      {hint && <p style={{ fontSize: 10.5, color: "var(--ink-soft)", marginTop: 4 }}>{hint}</p>}
    </div>
  );
}

export function AdvancedVariations({ value, onChange }) {
  const v = value || emptyAdvanced();
  const set = (next) => onChange({ ...v, ...next });

  return (
    <div style={{ background: "var(--surface)", borderRadius: 4, padding: "14px 16px", border: "1px solid var(--border)" }}>
      <p style={{ fontSize: 11.5, color: "var(--ink-soft)", margin: "0 0 12px", lineHeight: 1.5 }}>
        Add the choices customers pick (e.g. Colour <strong>and</strong> Size). Everything here is optional — fill only what you need. The <strong>+ Price</strong> box adds on top of your price (leave blank for one flat price); the <strong>Qty</strong> box (size only) is optional per-size stock.
      </p>
      <AdvRow label="COLOUR OPTIONS" addLabel="Add colour" rows={v.colour} onRows={r => set({ colour: r })} hint="Leave empty if this product has no colour choice." />
      <AdvRow label="SIZE OPTIONS" withStock addLabel="Add size" rows={v.size} onRows={r => set({ size: r })} hint="Qty = optional stock per size. If you set any, the overall stock becomes their total (otherwise use the Stock Qty field above)." />
      <div style={{ marginBottom: 4 }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.05em", color: "var(--ink-soft)", marginBottom: 6 }}><Icon n="sparkle" size="1.1em" inline /> CUSTOM OPTION (optional)</div>
        <input className="inp" placeholder="Name this option — e.g. Sleeve, Fabric…" value={v.custom?.label || ""} onChange={e => set({ custom: { ...v.custom, label: e.target.value } })} style={{ fontSize: 13, padding: "8px 10px", marginBottom: 6 }} />
        <AdvRow addLabel="Add value" rows={v.custom?.values || []} onRows={r => set({ custom: { ...v.custom, values: r } })} />
      </div>
    </div>
  );
}
